import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Modal, Space, Tooltip, Typography } from "antd";
import {
  mdiInformation,
  mdiPencil,
  mdiPlay,
  mdiTriangleSmallDown,
} from "@mdi/js";
import {
  PermissionVm,
  SimpleUserForUserRoleRelationVm,
} from "api/generated/lumen";
import { useDispatch, useSelector } from "app/store";
import { MaterialIcon } from "components/MaterialIcon";
import CheckboxDisplay from "components/ui/CheckboxDisplay";
import useTable from "hooks/useTable";
import CustomTable from "widgets/table/CustomTable";
import { ColumnStorageName } from "widgets/table/table-settings/ColumnStorageName";
import { ColumnConfigParams, FilterMode } from "widgets/table/useTableUtils";
import styles from "./UsersAndPermissionsAssign.module.scss";
import PermissionSelect from "./PermissionSelect/PermissionSelect";
import { GetUserPermissionRelationsRequestParams } from "./usersAndPermissionsAssign.store";
import { useBlocker } from "react-router-dom";
import useNavigationBlocker from "widgets/form/hooks/useNavigationBlocker";

const { confirm } = Modal;

type TransformedRelationType = Record<
  string,
  Record<string, Record<string, boolean | undefined>>
>;

const UsersAndPermissionsAssign = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [isEditing, setEditing] = useState<boolean>(false);
  const [selectedPermissions, setSelectedPermissions] = useState<
    PermissionVm[]
  >([]);

  const {
    users,
    listParams,
    permissions,
    relations,
    filterPermission,
    isSaveDisabled,
  } = useSelector((state) => state.usersAndPermissionsAssign);

  const { users: allUsers } = useSelector((state) => state.usersListSettings);

  const { getUserPermissionRelations } = useSelector(
    (state) => state.loading.effects.usersAndPermissionsAssign
  );

  useEffect(() => {
    dispatch.usersListSettings.fetchUsers({ pageSize: -1 });
  }, [dispatch.usersListSettings]);

  const changedValues = useRef<TransformedRelationType>({});

  const transformedRelations = useMemo(
    () =>
      relations?.reduce<TransformedRelationType>(
        (prev, { userId, permission, byRole }) => {
          if (!userId || !permission) {
            return prev;
          }

          if (!prev[userId]) {
            prev[userId] = {
              [permission]: { permissionRelation: true, byRole },
            };
          } else {
            prev[userId][permission] = { permissionRelation: true, byRole };
          }

          return prev;
        },
        {}
      ),
    [relations]
  );

  const addOrRemoveRelation = useCallback(
    ({
      userId,
      permission,
      checked,
    }: {
      userId: string;
      permission: string;
      checked: boolean;
    }) => {
      if (
        checked ===
        !!transformedRelations?.[userId]?.[permission]?.permissionRelation
      ) {
        delete changedValues.current[userId]?.[permission];

        if (Object.keys(changedValues.current[userId]).length === 0) {
          delete changedValues.current[userId];
        }

        if (Object.keys(changedValues.current).length === 0) {
          dispatch.usersAndPermissionsAssign.setIsSaveDisabled(true);
        } else {
          dispatch.usersAndPermissionsAssign.setIsSaveDisabled(false);
        }

        return;
      }

      changedValues.current = {
        ...changedValues.current,
        [userId]: {
          ...(changedValues.current[userId] ?? {}),
          [permission]: {
            permissionRelation: checked,
            byRole: !!transformedRelations?.[userId]?.[permission]?.byRole,
          },
        },
      };

      if (Object.keys(changedValues.current).length !== 0) {
        dispatch.usersAndPermissionsAssign.setIsSaveDisabled(false);
      }
    },
    [dispatch.usersAndPermissionsAssign, transformedRelations]
  );

  const filteredPermissions = useMemo(
    () => [
      ...((selectedPermissions?.length ? selectedPermissions : permissions) ??
        []),
    ],
    [permissions, selectedPermissions]
  );

  const fetchTableData = useCallback(
    (requestParams: GetUserPermissionRelationsRequestParams) => {
      return dispatch.usersAndPermissionsAssign.getUserPermissionRelations(
        requestParams
      );
    },
    [dispatch.usersAndPermissionsAssign]
  );

  const resetChangedValues = useCallback(() => {
    changedValues.current = {};
    fetchTableData(listParams);
    dispatch.usersAndPermissionsAssign.reset();
    dispatch.usersAndPermissionsAssign.setIsSaveDisabled(true);
  }, [dispatch.usersAndPermissionsAssign, fetchTableData, listParams]);

  const handleSaveRelations = useCallback(async () => {
    const relations = Object.entries(changedValues.current).flatMap(
      ([userId, permission]) =>
        Object.entries(permission).map(([permission, relation]) => ({
          userId,
          permission,
          relation: relation.permissionRelation,
        }))
    );

    if (relations) {
      confirm({
        okText: t("common.confirmDeleteModal.yes"),
        okType: "danger",
        cancelText: t("common.confirmDeleteModal.no"),
        title: t(
          "rolesAndPermissionsSettings.usersAndPermissionsAssign.confirmSaveModal.title"
        ),
        onOk: async () => {
          await dispatch.usersAndPermissionsAssign.updateUserPermissionRelations(
            {
              updateUserPermissionRelationsCommand: { relations },
            }
          );

          await fetchTableData({});

          setEditing(false);
          resetChangedValues();
        },
        onCancel: () => {
          resetChangedValues();
          setEditing(false);
        },
      });
    }
  }, [
    dispatch.usersAndPermissionsAssign,
    fetchTableData,
    resetChangedValues,
    t,
  ]);

  const blocker = useBlocker(({ currentLocation, nextLocation }) => {
    return (
      currentLocation.pathname !== nextLocation.pathname && !isSaveDisabled
    );
  });

  useNavigationBlocker({ blocker });

  const handleSortUsersWithPermission = useCallback(
    async (permission: PermissionVm) => {
      dispatch.usersAndPermissionsAssign.resetListParams();
      dispatch.usersAndPermissionsAssign.setFilterPermission(
        permission.name || ""
      );
      fetchTableData(listParams);
    },
    [dispatch.usersAndPermissionsAssign, fetchTableData, listParams]
  );

  const handleSortPermissionWithUser = useCallback(
    (userId: string) => {
      const filteredPermissionNames = relations
        ?.filter((relation) => relation.userId === userId)
        .map(({ permission }) => permission);

      const filteredCheckedPermissions =
        permissions?.filter((permission) =>
          filteredPermissionNames?.includes(permission.name)
        ) ?? [];

      setSelectedPermissions(filteredCheckedPermissions);
    },
    [permissions, relations]
  );

  const splittedUsers = useMemo(() => {
    return users?.map((user) => ({
      ...user,
      name: user.name?.split("\n"),
    }));
  }, [users]);

  const columnParams = useMemo<
    ColumnConfigParams<SimpleUserForUserRoleRelationVm>[]
  >(() => {
    return [
      {
        key: "userIds",
        dataIndex: "id",
        title: t("common.userName"),
        fixed: "left",
        width: "16rem",
        filterMode: FilterMode.FILTER_SELECT,
        filters: (allUsers || []).map((item) => ({
          text: `${item.familyname} ${item.forename}`,
          value: item.id || "",
        })),

        render: (_: any, record) => {
          const user = splittedUsers?.find(
            (user) => user.id === record.id
          )?.name;
          return (
            <Space className={styles.userCellWrapper}>
              <Space direction="vertical">
                <Typography.Text className={styles.userText}>
                  {user?.at(0)}
                </Typography.Text>
                <Typography.Text className={styles.userText}>
                  {user?.at(1)}
                </Typography.Text>
              </Space>
              <Space>
                <div
                  onClick={() => handleSortPermissionWithUser(record.id ?? "")}
                  className={styles.sortIcon}
                >
                  <MaterialIcon path={mdiPlay} size={0.75} />
                </div>
              </Space>
            </Space>
          );
        },
      },
      ...(filteredPermissions ?? []).map((permission) => {
        return {
          key: `permission-${permission.name}`,
          dataIndex: "id",
          width: "15rem",
          title: (
            <div className={styles.titleWrapper}>
              <Tooltip title={permission.description}>
                <MaterialIcon path={mdiInformation} />
              </Tooltip>
              <div className={styles.title}>
                <Tooltip title={permission.name} placement="topLeft">
                  {permission.name}
                </Tooltip>
              </div>
              <div
                onClick={() => handleSortUsersWithPermission(permission)}
                className={styles.sortIcon}
              >
                <MaterialIcon path={mdiTriangleSmallDown} size={1.1} />
              </div>
            </div>
          ),

          render: (userId: string) => {
            return (
              <Space className={styles.roleCell}>
                <CheckboxDisplay
                  byRole={
                    permission.name
                      ? {
                          ...changedValues.current,
                          ...transformedRelations,
                        }[userId]?.[permission.name]?.byRole ?? false
                      : false
                  }
                  disabled={
                    !isEditing || permission.name
                      ? {
                          ...changedValues.current,
                          ...transformedRelations,
                        }[userId]?.[permission.name ?? ""]?.byRole ?? false
                      : false
                  }
                  isEditing={isEditing}
                  value={
                    permission.name
                      ? {
                          ...changedValues.current,
                          ...transformedRelations,
                        }[userId]?.[permission.name]?.permissionRelation ??
                        false
                      : false
                  }
                  onChange={(checked) => {
                    if (!permission.name) {
                      return;
                    }

                    addOrRemoveRelation({
                      userId,
                      permission: permission.name,
                      checked,
                    });
                  }}
                />
              </Space>
            );
          },
        };
      }),
      {
        key: "emptyCol",
        fixed: "right",
        render: () => <></>,
      },
    ];
  }, [
    addOrRemoveRelation,
    allUsers,
    filteredPermissions,
    handleSortPermissionWithUser,
    handleSortUsersWithPermission,
    isEditing,
    splittedUsers,
    t,
    transformedRelations,
  ]);

  const customTableProps = useTable({
    fetchTableData,
    columnParams,
    list: users,
    listParams,
    columnStorageName: ColumnStorageName.USERS_AND_PERMISSIONS_ASSIGN,
    isResetCustomFilters: !!selectedPermissions.length || !!filterPermission,
    selectMode: "multiple",
    isFilterOption: false,
    roleSelect: (
      <PermissionSelect
        selectedPermissions={selectedPermissions}
        handleSelectedPermissions={(e: PermissionVm[]) => {
          setSelectedPermissions(e);
        }}
      />
    ),
    disableTableSettings: true,
    headerOptions: (
      <Space>
        {isEditing ? (
          <Space>
            <Button
              type="text"
              onClick={() => {
                if (!isSaveDisabled) {
                  confirm({
                    okText: t("common.confirmDeleteModal.yes"),
                    okType: "danger",
                    cancelText: t("common.confirmDeleteModal.no"),
                    title: t(
                      "rolesAndPermissionsSettings.usersAndPermissionsAssign.confirmCancelModal.title"
                    ),
                    onOk: async () => {
                      setEditing(false);
                      resetChangedValues();
                    },
                  });
                } else {
                  setEditing(false);
                }
              }}
            >
              {t("common.cancel")}
            </Button>

            <Button
              type="primary"
              loading={getUserPermissionRelations.loading}
              onClick={handleSaveRelations}
              disabled={isSaveDisabled}
            >
              {t("common.save")}
            </Button>
          </Space>
        ) : (
          <Button
            icon={<MaterialIcon path={mdiPencil} />}
            loading={getUserPermissionRelations.loading}
            onClick={() => setEditing(true)}
          >
            {t("common.edit")}
          </Button>
        )}
      </Space>
    ),
    resetStore: dispatch.usersAndPermissionsAssign.reset,
    resetListParams: () => {
      setSelectedPermissions([]);
      dispatch.usersAndPermissionsAssign.setFilterPermission(null);
      dispatch.usersAndPermissionsAssign.resetListParams();
    },
    loading: getUserPermissionRelations.loading,
  });

  return (
    <CustomTable<SimpleUserForUserRoleRelationVm>
      {...customTableProps}
      scrollX={"max-content"}
      className={styles.wrapper}
      selectable={false}
      pageHasTabs
    />
  );
};

export default UsersAndPermissionsAssign;
